import { Time, createChart } from "lightweight-charts";
import { useEffect, useRef, useState } from "react";


interface RocketRangeLTWProps {
    from: bigint;
    to: bigint;
    openPrice: bigint
}

const RocketRangeLTW: React.FC<RocketRangeLTWProps> = ({ from, to, openPrice }) => {

    const [data, setData] = useState<any[]>([]);
    const chartContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (from && to)
            fetch(`https://api.diadata.org/v1/assetChartPoints/MA120/Alephium/tgx7VNFoP9DJiFMFgXXtafQZkUvyEdDHT9ryamHJYrjq?endtime=${to / 1000n}&starttime=${from / 1000n}`).then(x => x.json()).then(x => {
                if (x.DataPoints && x.DataPoints[0].Series) {
                    const mapped = x.DataPoints[0].Series[0].values.map(y => { return { time: (new Date(y[0])).getTime() / 1000, value: y[6] } });
                    setData(mapped.reverse());
                }
            });
    }, [from, to])

    const bigInt8ToNumber = (a: bigint) => Number(a) / 10 ** 8;
    const calculatePriceRange = (data) => {
        if (!data || data.length === 0) return { min: 0, max: 0 };
        const prices = data.map((point) => point.value);
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        const rangePadding = (maxPrice - minPrice) * 0.1; // 10% padding
        return {
            min: minPrice - rangePadding,
            max: maxPrice + rangePadding,
        };
    };

    const calculateTimeRange = (data) => {
        if (!data || data.length === 0) return { from: 0, to: 0 };
        const times = data.map((point) => point.time);
        return {
            from: Math.min(...times),
            to: Math.max(...times),
        };
    };


    useEffect(() => {
        if (!chartContainerRef.current || data.length === 0) return;
        // Create a chart
        const chart = createChart(chartContainerRef.current, {
            width: chartContainerRef.current.offsetWidth,
            height: 300,
            timeScale: {
                timeVisible: true, // Show time instead of dates
                secondsVisible: false, // Hide seconds for clarity

            },
            layout: {
                background: { color: "rgb(17, 24, 39)" },
                textColor: "#d1d4dc",
            },
            grid: {
                vertLines: {
                    color: "rgba(42, 46, 57, 0.0)",
                },
                horzLines: {
                    color: "rgba(42, 46, 57, 0.0)",
                },
            },
        });

        // Create an area series
        const areaSeries = chart.addAreaSeries({
            topColor: "rgba(239, 68, 68, 0.7)",
            bottomColor: "rgba(239, 68, 68, 0.3)",
            lineColor: "rgba(239, 68, 68, 1)",
            lineWidth: 2,
            priceFormat: {
                type: "price",
                precision: 3,
                minMove: 0.001
            }
        });

        // Set the data
        areaSeries.setData(data);


        // Define game win areas
        const winAreas = [
            { price: bigInt8ToNumber(openPrice) * 1.04, color: "rgb(34, 197, 94)", label: "+4%" },
            { price: bigInt8ToNumber(openPrice) * 1.01, color: "rgb(74, 222, 128)", label: "+1%" },
            { price: bigInt8ToNumber(openPrice) * 0.99, color: "rgb(248, 113, 113)", label: "-1%" },
            { price: bigInt8ToNumber(openPrice) * 0.96, color: "rgb(239, 68, 68)", label: "-4%" },
        ];


        // Add price lines
        winAreas.forEach((area) => {
            areaSeries.createPriceLine({
                price: area.price,
                color: area.color,
                lineWidth: 2,
                lineStyle: 0, // Solid line
                axisLabelVisible: true,
                title: area.label,
            });
        });

        chart.timeScale().setVisibleRange({
            from: Number(from)/1000 as Time,
            to:  Number(to)/1000 as Time,
        })
        
        chart.priceScale('right').applyOptions({
            autoScale: false, // Disable auto-scaling
            scaleMargins: {
                bottom: 0.3,
                top: 0.3
            }
        });


        // Handle resizing
        const handleResize = () => {
            if (chartContainerRef && chartContainerRef.current)
                chart.applyOptions({ width: chartContainerRef.current.offsetWidth });
        };

        window.addEventListener("resize", handleResize);

        // Cleanup
        return () => {
            chart.remove();
            window.removeEventListener("resize", handleResize);
        };
    }, [data]);


    return <div ref={chartContainerRef} style={{ width: "100%", height: "300px" }} />;
};

export default RocketRangeLTW;